/**
 * @file Region DOM Manager
 * @description DOM element management and caching for region component
 */

import { SELECTORS } from "./constants.js";

// DOM element cache
let domElements = {};

/**
 * Initialize DOM element cache for better performance
 */
export function initializeDOMElements() {
  domElements = {
    provinceListing: document.getElementById("province-listing"),
    regionBody: document.getElementById("region-body"),
    mainDescription: document.querySelector(SELECTORS.MAIN_DESCRIPTION),
    regionDescription: document.querySelector(SELECTORS.REGION_DESCRIPTION),
    vietnamMap: document.getElementById("vietnam-map"),
    loadingIndicator: document.getElementById("loading-indicator"),
    mapsWrapper: document.getElementById("maps-wrapper"),
    mapWrapper: document.getElementById("map-wrapper"),
    regionSelect: document.getElementById("region-select"),
    turnBackBtn: document.getElementById("turnBackBtn"),
    osmWrapper: document.getElementById("osm-wrapper"),
  };
}

/**
 * Get DOM element by key
 * @param {string} key - Element key
 * @returns {HTMLElement|null} DOM element
 */
export function getElement(key) {
  return domElements[key] || null;
}

/**
 * Get all DOM elements
 * @returns {Object} All DOM elements
 */
export function getAllElements() {
  return { ...domElements };
}

/**
 * Check if element exists
 * @param {string} key - Element key
 * @returns {boolean} Whether element exists
 */
export function hasElement(key) {
  return !!domElements[key];
}

/**
 * Get element by selector
 * @param {string} selector - CSS selector
 * @returns {HTMLElement|null} DOM element
 */
export function getElementBySelector(selector) {
  return document.querySelector(selector);
}

/**
 * Get elements by selector
 * @param {string} selector - CSS selector
 * @returns {NodeList} DOM elements
 */
export function getElementsBySelector(selector) {
  return document.querySelectorAll(selector);
}

/**
 * Get element by ID
 * @param {string} id - Element ID
 * @returns {HTMLElement|null} DOM element
 */
export function getElementById(id) {
  return document.getElementById(id);
}

/**
 * Create DOM element
 * @param {string} tagName - HTML tag name
 * @param {Object} attributes - Element attributes
 * @returns {HTMLElement} Created element
 */
export function createElement(tagName, attributes = {}) {
  const element = document.createElement(tagName);

  // Set attributes
  Object.entries(attributes).forEach(([key, value]) => {
    if (key === "textContent") {
      element.textContent = value;
    } else if (key === "innerHTML") {
      element.innerHTML = value;
    } else {
      element.setAttribute(key, value);
    }
  });

  return element;
}

/**
 * Update element content
 * @param {HTMLElement} element - DOM element
 * @param {string} content - Content to set
 * @param {string} type - Content type ('textContent' or 'innerHTML')
 */
export function updateElementContent(element, content, type = "innerHTML") {
  if (!element) return;

  if (type === "textContent") {
    element.textContent = content;
  } else {
    element.innerHTML = content;
  }
}

/**
 * Add class to element
 * @param {HTMLElement} element - DOM element
 * @param {string} className - Class name to add
 */
export function addClass(element, className) {
  if (element && className) {
    element.classList.add(className);
  }
}

/**
 * Remove class from element
 * @param {HTMLElement} element - DOM element
 * @param {string} className - Class name to remove
 */
export function removeClass(element, className) {
  if (element && className) {
    element.classList.remove(className);
  }
}

/**
 * Toggle class on element
 * @param {HTMLElement} element - DOM element
 * @param {string} className - Class name to toggle
 */
export function toggleClass(element, className) {
  if (element && className) {
    element.classList.toggle(className);
  }
}

/**
 * Set element style
 * @param {HTMLElement} element - DOM element
 * @param {string} property - CSS property
 * @param {string} value - CSS value
 */
export function setStyle(element, property, value) {
  if (element && property && value !== undefined) {
    element.style[property] = value;
  }
}

/**
 * Set multiple element styles
 * @param {HTMLElement} element - DOM element
 * @param {Object} styles - Styles object
 */
export function setStyles(element, styles) {
  if (!element || !styles) return;

  Object.entries(styles).forEach(([property, value]) => {
    setStyle(element, property, value);
  });
}

/**
 * Get element dataset
 * @param {HTMLElement} element - DOM element
 * @param {string} key - Dataset key
 * @returns {string|null} Dataset value
 */
export function getDataset(element, key) {
  return element?.dataset?.[key] || null;
}

/**
 * Set element dataset
 * @param {HTMLElement} element - DOM element
 * @param {string} key - Dataset key
 * @param {string} value - Dataset value
 */
export function setDataset(element, key, value) {
  if (element && key && value !== undefined) {
    element.dataset[key] = value;
  }
}

/**
 * Clear DOM element cache
 */
export function clearCache() {
  domElements = {};
}

/**
 * Get cache statistics
 * @returns {Object} Cache statistics
 */
export function getCacheStats() {
  const keys = Object.keys(domElements);
  const validElements = keys.filter((key) => domElements[key] !== null);

  return {
    totalKeys: keys.length,
    validElements: validElements.length,
    nullElements: keys.length - validElements.length,
    keys: keys,
  };
}
